import { ChevronRight } from 'lucide-react';
import Badge from './Badge';

/**
 * Item del menú lateral del producto. Layout: icon + label + descripción
 * inline opcional + chevron derecho. Sin background, separación por
 * spacing/rules controlado por el contenedor padre.
 *
 * MenuItem es para navegación (compacto, label prominente, descripción
 * opcional como hint); las listas de contenido van como fila editorial.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.icon]
 * @param {string} props.label
 * @param {string} [props.description]
 * @param {'pae'|'prepara-internado'|'prepara-turno'|'revisa'|'bitacora'|'progreso'} [props.module]
 *   No aplica si variant='danger' (se ignora — el color del danger gana).
 * @param {React.ReactNode} [props.badge]   Default si disabled y sin badge: "Pronto".
 * @param {boolean} [props.chevron=false]
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.active=false]
 * @param {'default'|'danger'} [props.variant='default']
 *   `danger` colorea icon + label + descripción en `clinical-critical`
 *   (rojo del sistema clínico). Para acciones destructivas tipo "Cerrar
 *   sesión" o "Eliminar cuenta".
 * @param {() => void} [props.onClick]
 */
export default function MenuItem({
  icon,
  label,
  description,
  module,
  badge,
  chevron = false,
  disabled = false,
  active = false,
  variant = 'default',
  onClick,
}) {
  const isDanger = variant === 'danger';

  // Color del icon: danger gana sobre module sobre ink-muted.
  const iconColorClass = isDanger
    ? 'text-clinical-critical'
    : module
    ? MODULE_ICON_CLASSES[module] || 'text-ink-muted'
    : 'text-ink-muted';

  // Label: en danger toma el rojo clínico.
  const labelClass = isDanger
    ? 'text-clinical-critical font-medium'
    : active
    ? 'text-ink font-semibold'
    : 'text-ink font-medium';

  // Descripción: en danger sutil, opacity 70 sobre el rojo.
  const descriptionClass = isDanger
    ? 'text-clinical-critical/70'
    : 'text-ink-muted';

  // Hover: en danger un tinte muy sutil del rojo de fondo; en default surface-2.
  const bgClass = isDanger
    ? 'bg-transparent hover:bg-clinical-critical-bg/50'
    : active
    ? 'bg-surface-2'
    : 'bg-transparent hover:bg-surface-2';

  const focusRing =
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-inset';

  const resolvedBadge = badge || (disabled ? <Badge>Pronto</Badge> : null);

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={[
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-tk-sm text-left font-sans',
        'transition-colors duration-150',
        bgClass,
        focusRing,
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
    >
      {icon && (
        <span className={`flex-shrink-0 ${iconColorClass}`} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="flex-1 min-w-0">
        <span className={`block text-body ${labelClass}`}>{label}</span>
        {description && (
          <span className={`block text-small mt-0.5 ${descriptionClass}`}>
            {description}
          </span>
        )}
      </span>
      {resolvedBadge && <span className="flex-shrink-0">{resolvedBadge}</span>}
      {chevron && (
        <ChevronRight
          size={14}
          strokeWidth={1.6}
          className={`flex-shrink-0 ${isDanger ? 'text-clinical-critical/60' : 'text-ink-faint'}`}
          aria-hidden="true"
        />
      )}
    </button>
  );
}

// DEUDA TÉCNICA — este mapping está duplicado en:
//   - foundational/ModuleHeader.jsx (MODULE_TOKENS)
//   - foundational/HomeCard.jsx (MODULE_ICON_CLASSES + MODULE_BG_VARS)
//   - foundational/Badge.jsx (MODULE_CLASSES)
//   - foundational/MenuItem.jsx (MODULE_ICON_CLASSES)
// Cuando agregués un módulo nuevo: actualizar las 4 entradas.
// Refactor a una fuente única de verdad pendiente (post-stores).
const MODULE_ICON_CLASSES = {
  pae:                 'text-module-pae',
  'pae-rapido':        'text-module-pae-rapido',
  'pae-detallado':     'text-module-pae-detallado',
  'prepara-practica':  'text-module-prepara-practica',
  'prepara-internado': 'text-module-prepara-internado',
  'prepara-turno':     'text-module-prepara-turno',
  revisa:              'text-module-revisa',
  bitacora:            'text-module-bitacora',
  calendario:          'text-module-calendario',
  progreso:            'text-module-progreso',
  escalas:             'text-module-escalas',
  medicamentos:        'text-module-medicamentos',
  respira:             'text-module-respira',
  bienestar:           'text-module-bienestar',
  enciclopedia:        'text-module-enciclopedia',
  bibliografia:        'text-module-bibliografia',
  'prepara-examen':    'text-module-prepara-examen',
  ges:                 'text-module-ges',
};
